import type { ReactNode, CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   PageHero — Banner premium reutilizável
   Uso:
     <PageHero
       icon={TrendingUp}
       category="Comercial"
       title="Oportunidades"
       subtitle="Gestão de leads"
       gradient="indigo"
       inlineStats
       stats={[{ icon: DollarSign, value: 'R$ 150k', label: 'Pipeline', color: '#34d399' }]}
       action={{ label: 'Novo', icon: Plus, onClick: fn }}
     />
   ═══════════════════════════════════════════════════════════════ */

export interface HeroStat {
  icon: LucideIcon;
  value: string | number;
  label: string;
  color: string;
}

export interface HeroAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
}

const GRADIENTS: Record<string, string> = {
  indigo: 'linear-gradient(135deg, #312e81 0%, #3730a3 40%, #4f46e5 100%)',
  blue: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 40%, #3b82f6 100%)',
  emerald: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #10b981 100%)',
  rose: 'linear-gradient(135deg, #881337 0%, #9f1239 40%, #f43f5e 100%)',
  slate: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #475569 100%)',
  amber: 'linear-gradient(135deg, #78350f 0%, #92400e 40%, #f59e0b 100%)',
};

const ACCENT_COLORS: Record<string, string> = {
  indigo: '#c7d2fe',
  blue: '#93c5fd',
  emerald: '#6ee7b7',
  rose: '#fda4af',
  slate: '#cbd5e1',
  amber: '#fde68a',
};

export interface PageHeroProps {
  icon?: LucideIcon;
  category?: string;
  title: string;
  subtitle?: string | ReactNode;
  gradient?: keyof typeof GRADIENTS | string;
  stats?: HeroStat[];
  /** Coloca stats inline entre título e botão (layout compacto) */
  inlineStats?: boolean;
  action?: HeroAction;
  /** Extra content after stats */
  children?: ReactNode;
  /** Custom className on wrapper */
  className?: string;
  /** Custom style on wrapper */
  style?: CSSProperties;
}

export function PageHero({
  icon: Icon,
  category,
  title,
  subtitle,
  gradient = 'indigo',
  stats,
  inlineStats = false,
  action,
  children,
  className = '',
  style,
}: PageHeroProps) {
  const bg = GRADIENTS[gradient] || gradient;
  const accent = ACCENT_COLORS[gradient as string] || '#c7d2fe';

  const renderStatsGrid = () => {
    if (!stats?.length) return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`, gap: 10, marginTop: 12 }}>
        {stats.map((s, i) => (
          <div key={i} className="kb-stat-glass">
            <div className="kb-stat-icon" style={{ background: `${s.color}15`, color: s.color }}>
              <s.icon size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="kb-stat-value">{s.value}</div>
              <div className="kb-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStatsInline = () => {
    if (!stats?.length) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center', overflow: 'hidden' }}>
        {stats.map((s, i) => (
          <div
            key={i}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,.1)', backdropFilter: 'blur(6px)',
              borderRadius: 12, padding: '8px 14px 8px 10px',
              border: '1px solid rgba(255,255,255,.08)',
              flexShrink: 0,
            }}
          >
            {/* Ícone em container arredondado */}
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: `${s.color}22`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <s.icon size={16} style={{ color: s.color }} />
            </div>
            {/* Label + Valor empilhados */}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: 'rgba(255,255,255,.55)', fontSize: '.62rem', fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                {s.label}
              </div>
              <div style={{ color: 'white', fontSize: '.88rem', fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                {s.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Action button agora é renderizado como flutuante no wrapper principal

  return (
    <div
      className={className}
      style={{
        background: bg,
        borderRadius: 18,
        padding: 'clamp(12px, 1.4vw, 18px)',
        marginBottom: 12,
        position: 'relative',
        overflow: 'visible',
        ...style,
      }}
    >
      {/* ── Botão flutuante (canto superior direito) ── */}
      {action && (
        <button
          onClick={action.onClick}
          style={{
            position: 'absolute', top: -6, right: 12, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 10,
            background: accent, color: GRADIENTS[gradient as string] ? gradient === 'indigo' ? '#312e81' : '#1e3a5f' : '#1e293b',
            fontWeight: 700, fontSize: '.8rem', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,.2)',
          }}
        >
          {action.icon && <action.icon size={14} />}
          {action.label}
        </button>
      )}
      {/* Decorative circle */}
      <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.06)' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {inlineStats ? (
          /* ── Layout inline: [titulo] [stats] [botão] ── */
          <>
            {(Icon || category) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                {Icon && <Icon size={16} style={{ color: accent }} />}
                {category && (
                  <span style={{ color: 'rgba(255,255,255,.45)', fontSize: '.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                    {category}
                  </span>
                )}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flexShrink: 0 }}>
                <h1 style={{ color: 'white', fontSize: 'clamp(1.05rem, 1.6vw, 1.28rem)', fontWeight: 800, margin: 0 }}>
                  {title}
                </h1>
              </div>
              {renderStatsInline()}
            </div>
          </>
        ) : (
          /* ── Layout padrão: grid abaixo ── */
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                {(Icon || category) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                    {Icon && <Icon size={16} style={{ color: accent }} />}
                    {category && (
                      <span style={{ color: 'rgba(255,255,255,.45)', fontSize: '.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                        {category}
                      </span>
                    )}
                  </div>
                )}
                <h1 style={{ color: 'white', fontSize: 'clamp(1.05rem, 1.6vw, 1.28rem)', fontWeight: 800, margin: 0 }}>
                  {title}
                </h1>
                {subtitle && (
                  <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.75rem', marginTop: 2 }}>
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            {renderStatsGrid()}
          </>
        )}

        {children}
      </div>
    </div>
  );
}

